<script>
  import ApplicationState from "../../../../../Stores/applicationStates"
  import notify from "../../../../../Script/notify"
  import DeleteConfirmation from "../../../Email/Shared/deleteConfirmation.svelte"
  import DuplicateConfirmation from "../../../Email/Shared/duplicateConfirmation.svelte"
  import { afterUpdate, createEventDispatcher } from "svelte"
  import { loadTooltip } from "../../../../../Script/loadTooltip"
  import { deleteRequest, getRequest, postRequest } from "../../../../../Script/apiMethods"

  const dispatch = createEventDispatcher()

  /**
   * @type {import("../../../../../Stores/templatesList").Item}
   */

  export let template = {}

  /**
   * @type {Element} loadingScreen - Represents the loading screen element.
   */
  export let loadingScreen

  /**
   * @type {string} loadingText -  The text displayed on the loading screen.
   * @default ""
   */
  export let loadingText = ""

  /**
   * @type {Number} overall document count
   * @default ""
   */
  export let count = ""

  /**
   * @type {Boolean} for showing the Delete Email Template Confirmation Modal
   */
  let showDeleteModal = false
  /**
   * @type {Boolean} whether to show duplicate modal or not
   */
  let showDuplicateModal = false
  /**
   * @type {String} - to specify document type in delete confirmation Modal
   */
  let templateType = "Document"

  /**
   * Deletes a document template.
   *
   * @param {number} deleteId - The ID of the template to delete.
   * @returns {Promise<void>} - A promise that resolves once the template is deleted.
   */
  const deleteTemplate = async (deleteId) => {
    try {
      // Set loadingScreen to true to indicate that the deletion is in progress
      loadingScreen = true
      // Set showDeleteModal to false to hide the deletion modal
      showDeleteModal = false

      // Set the loadingText to display a loading message
      loadingText = "Deleting&nbsp;<strong>Document Template</strong>&nbsp;"

      await deleteRequest(`/api/swagger/v2/templates/documents/${deleteId}`, {
        headers: {
          "x-access-token": $ApplicationState.token,
        },
      })

      dispatch("fetchList")
      // Show a success notification for the deletion
      notify.success(`Document Template <strong style="hyphens: auto;">${template.name}</strong> successfully <strong>deleted</strong>`)
    } catch (error) {
      deleteDocNotification.close()
      const info = "PAGE: Document template list\nEVENT: While deleting document template"
      //throws error as danger notification
      deleteDocNotification = notify.danger(error, info)
    } finally {
      // Set loadingScreen to false to indicate that the deletion is complete
      loadingScreen = false
    }
  }

  const duplicateTemplate = async () => {
    try {
      showDuplicateModal = false
      loadingText = `Duplicating&nbsp;<strong>${template.name} Document Template</strong>&nbsp;`
      loadingScreen = true

      const CONFIG = {
        headers: {
          "x-access-token": $ApplicationState.token,
        },
      }

      // Fetching document template data
      const { template: documentData } = await getRequest(`/api/swagger/v2/templates/documents/${template.id}`, CONFIG)

      // Fetching document list
      const { templates } = await getRequest(`/api/swagger/v2/templates/documents?limit=${count}&page=1&sort=-1&sortBy=date`, CONFIG)

      let tempArray = templates.filter((document) => document.name.includes(`${documentData.name}`))

      let copyCount = 0
      for (let i = 0; i < tempArray.length; i++) {
        if (documentData.name.length + 4 == tempArray[i].name.length) copyCount++
      }

      let tempValue = ` ${copyCount ? `(${copyCount + 1})` : `(1)`}`
      // let tempValue = ` ${tempArray.length ? `(${tempArray.length + 1})` : `(1)`}`

      let remainingLength = 30 - tempValue.length

      // Make sure the documentData.name doesn't exceed the remaining length
      let truncatedName = documentData.name.slice(0, remainingLength)

      let templateName = truncatedName + tempValue

      let { created, modified, ...filteredData } = documentData

      let reqBody = { ...filteredData, name: templateName }

      reqBody.file = ""
      reqBody.email = ""

      const { template: newTemplate } = await postRequest("/api/swagger/v2/templates/documents", reqBody, CONFIG)

      dispatch("fetchList")

      notify.success(`<strong>${newTemplate.name}</strong> created successfully.`)
    } catch (error) {
      // Show an error notification
      const info = "PAGE: Document template list\nEVENT: While duplicating document template"
      notify.danger(error, info)
    } finally {
      loadingScreen = false
    }
  }

  afterUpdate(() => {
    loadTooltip()
    window.addEventListener("resize", loadTooltip)
  })
</script>

{#if showDeleteModal}
  <DeleteConfirmation
    on:click={() => {
      showDeleteModal = false
    }}
    {template}
    DocumentType={templateType}
    on:delete={() => {
      deleteTemplate(template.id)
    }}
  />
{/if}

{#if showDuplicateModal}
  <DuplicateConfirmation
    on:click={() => {
      showDuplicateModal = false
    }}
    type="Document"
    on:duplicate={duplicateTemplate}
    templateName={template.name}
  />
{/if}

{#if template.name}
  <div class="flex w-full justify-between rounded-md border-2 border-transparent bg-white p-3 text-white hover:shadow-md dark:bg-lightgray dark:hover:border-blue-500">
    <div class="flex flex-col truncate pl-2">
      <h2 title={template.name} class="select-none truncate text-sm font-bold text-gray-d00 dark:text-white/70 md:text-lg">
        {template.name}
      </h2>
      <h2 class="select-none truncate text-xs font-medium text-gray-500 md:text-sm">
        <!-- {new Date(template.created).toString().slice(0, 24)} -->
        {new Date(template.modified || template.created).toString().slice(0, 24)}
      </h2>
    </div>
    <div class="flex items-center justify-end px-3 text-gray-100">
      <a name="tooltip" id="Edit" tooltip-position="top" style="text-decoration: none;" href="#/templates/document/edit?id={template.id}" class="flex cursor-pointer select-none items-center justify-center rounded-full px-3 py-2 text-sm font-medium text-yellow-500 hover:bg-yellow-500 hover:text-white focus:outline-none active:text-white peer-hover:text-white sm:px-5">
        <i class="fa-solid fa-pen-field" />
        <span class="hidden lg:block">&nbsp;&nbsp;Edit</span>
      </a>

      <button
        name="tooltip"
        id="Duplicate"
        tooltip-position="top"
        on:click={() => {
          showDuplicateModal = true
        }}
        class="flex cursor-pointer select-none items-center justify-center rounded-full px-3 py-2 text-sm font-medium text-emerald-500 hover:bg-emerald-500 hover:text-white focus:outline-none active:bg-emerald-400 active:text-white md:px-5 xl:w-32"
      >
        <i class="fa-solid fa-clone" />
        <span class="hidden lg:block">&nbsp;&nbsp;Duplicate</span>
      </button>

      <button name="tooltip" id="Delete" tooltip-position="top" on:click={() => (showDeleteModal = true)} class="flex cursor-pointer select-none items-center justify-center rounded-full px-3 py-2 text-sm font-medium text-red-500 hover:bg-red-500 hover:text-white focus:outline-none active:text-white sm:px-5">
        <i class="fa-solid fa-trash-can" />
        <span class="hidden lg:block">&nbsp;&nbsp;Delete</span>
      </button>
    </div>
  </div>
{/if}
